import { useNavigate } from "react-router-dom";
import { Truck, PackageOpen, ClipboardCheck, PlusSquare, Trash2, ScanLine, LogOut, Building2, Boxes } from "lucide-react";
import { BleReaderButton } from "@/components/BleReaderButton";

/**
 * Home screen — 6 tiles for the supported PDA workflows.
 * No "Orders / Customers / Reports" stuff from the desktop app; this
 * screen is intentionally a dead-end: scan or back-to-login.
 */
export default function PdaMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const tiles = [
    { key: "loading", label: "Truck Loading", desc: "Scan bags onto truck", icon: Boxes, color: "bg-orange-600", testid: "pda-tile-loading" },
    { key: "dispatch", label: "Dispatch", desc: "Deliver to hotels", icon: Truck, color: "bg-emerald-600", testid: "pda-tile-dispatch" },
    { key: "receiving", label: "Receiving", desc: "Dirty linen in", icon: PackageOpen, color: "bg-sky-600", testid: "pda-tile-receiving" },
    { key: "stocktake", label: "Warehouse Stocktake", desc: "Warehouse count", icon: ClipboardCheck, color: "bg-violet-600", testid: "pda-tile-stocktake" },
    { key: "stocktake-hotel", label: "Hotel Stocktake", desc: "Count at a hotel", icon: Building2, color: "bg-indigo-600", testid: "pda-tile-stocktake-hotel" },
    { key: "identify", label: "Identify", desc: "Look up a tag", icon: ScanLine, color: "bg-amber-600", testid: "pda-tile-identify" },
    { key: "register", label: "Register", desc: "Tag new linen", icon: PlusSquare, color: "bg-rose-600", testid: "pda-tile-register" },
    { key: "delete", label: "Delete", desc: "Retire linen", icon: Trash2, color: "bg-neutral-700", testid: "pda-tile-delete" },
  ];

  return (
    <div className="min-h-screen bg-neutral-900 text-white px-4 py-4 flex flex-col" data-testid="pda-menu">
      <header className="flex items-center mb-4">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-neutral-400">Signed in</div>
          <div className="font-display font-bold text-lg truncate">{user?.username || user?.email}</div>
        </div>
        <BleReaderButton />
        <button
          onClick={() => { onLogout?.(); navigate("/pda/login", { replace: true }); }}
          data-testid="pda-menu-logout"
          className="h-11 px-3 rounded-md border border-neutral-700 flex items-center gap-2 hover:bg-neutral-800"
        >
          <LogOut className="h-4 w-4" /> <span className="text-sm">Logout</span>
        </button>
      </header>

      <div className="grid grid-cols-2 gap-3 flex-1 content-start">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => navigate(`/pda/${t.key}`)}
              className={`${t.color} rounded-xl p-4 text-left aspect-square flex flex-col justify-between hover:brightness-110 active:scale-95 transition`}
            >
              <Icon className="h-10 w-10 opacity-90" />
              <div>
                <div className="font-display font-bold text-xl">{t.label}</div>
                <div className="text-xs opacity-80">{t.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-800 flex items-center justify-between text-[11px] text-neutral-500">
        <span>DRRT PDA · simplified mode</span>
        {/* Drivers are PDA-only — hide the escape hatch so they can't wander
            into the desktop admin app (which they don't have permissions for
            anyway and would just see a blank screen). */}
        {user?.role !== "driver" && (
          <button
            onClick={() => { window.location.href = "/admin"; }}
            data-testid="pda-menu-full-mode"
            className="text-neutral-300 underline"
          >
            Open full web app
          </button>
        )}
      </div>
    </div>
  );
}
